import { useEffect, useState, useMemo, Fragment } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { INR, formatApiError, fmtDMY, API } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Download, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PageSpinner } from "@/components/Spinner";

function daysBetween(fromISO, toISO) {
  const a = new Date(fromISO + "T00:00:00");
  const b = new Date(toISO + "T00:00:00");
  return Math.round((b - a) / 86400000);
}

function DueByChip({ dueDate }) {
  const today = new Date().toISOString().slice(0, 10);
  const delta = daysBetween(today, dueDate);
  if (delta === 0) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[color:var(--pending-bg)] text-[color:var(--pending)] text-[11px] font-semibold">
        Due today
      </span>
    );
  }
  if (delta > 0) {
    const tone =
      delta <= 3
        ? "bg-[color:var(--pending-bg)] text-[color:var(--pending)]"
        : "bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)]";
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[11px] font-semibold ${tone}`}>
        Due in {delta} {delta === 1 ? "day" : "days"}
      </span>
    );
  }
  const overdue = -delta;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[color:var(--overdue-bg)] text-[color:var(--overdue)] text-[11px] font-semibold">
      {overdue} {overdue === 1 ? "day" : "days"} overdue
    </span>
  );
}

function PaidChip({ dueDate, paymentDate }) {
  if (!paymentDate) return <span className="text-ink-2 text-xs">-</span>;
  const late = daysBetween(dueDate, paymentDate);
  if (late <= 0) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[color:var(--paid-bg)] text-[color:var(--paid)] text-[11px] font-semibold">
        Paid on time
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-[color:var(--pending-bg)] text-[color:var(--pending)] text-[11px] font-semibold">
      Paid {late} {late === 1 ? "day" : "days"} late
    </span>
  );
}

export default function PortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({}); // { invoiceId: true }
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    axios
      .get(`${API}/portal/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const download = async (fileId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/portal/${token}/files/${fileId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Memoize derived lists so we don't re-filter/sort on every render.
  const outstanding = useMemo(
    () => (data?.invoices || []).filter((i) => i.status !== "paid"),
    [data?.invoices]
  );
  const paid = useMemo(
    () => (data?.invoices || []).filter((i) => i.status === "paid").slice(0, 10),
    [data?.invoices]
  );
  const sortedPaymentsByInvoiceId = useMemo(() => {
    const map = {};
    for (const inv of outstanding) {
      if (inv.payments && inv.payments.length > 0) {
        map[inv.id] = inv.payments
          .slice()
          .sort((a, b) => (a.payment_date || "").localeCompare(b.payment_date || ""));
      }
    }
    return map;
  }, [outstanding]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-bone p-8">
        <div className="max-w-md text-center">
          <div className="eyebrow">Statement</div>
          <h1 className="text-2xl mt-2">Link not valid</h1>
          <p className="text-sm text-ink-2 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <PageSpinner />;

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-white py-5 px-4 sm:py-6 sm:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/aisensy-dark.svg" alt="AiSensy" className="h-6 w-auto" />
            <span className="text-[11px] uppercase tracking-[0.14em] text-ink-2">
              Statement portal
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API}/portal/${token}/statement.pdf`}
              download
              className="inline-flex items-center gap-1.5 bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white text-xs sm:text-[13px] font-medium px-3 py-2 rounded-sm transition-colors"
              data-testid="portal-download-statement-btn"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Download statement</span>
              <span className="sm:hidden">PDF</span>
            </a>
            <div className="text-xs text-ink-2 tabular hidden sm:block">
              {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
        <div>
          <div className="eyebrow">Account statement for</div>
          <h1 className="text-xl sm:text-2xl mt-1 break-words">{data.entity.name}</h1>
          <p className="text-sm text-ink-2 mt-1">
            {data.entity.type === "client" ? "Client" : "Partner"}
            {data.entity.payment_timeline_days
              ? ` · ${data.entity.payment_timeline_days}-day payment terms`
              : ""}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-5 sm:p-6">
            <div className="eyebrow">Total outstanding</div>
            <div className="tabular text-2xl sm:text-3xl mt-2 text-[color:var(--aisensy-dark)] break-words">
              {INR(data.outstanding)}
            </div>
            <div className="text-xs text-ink-2 mt-2">
              {outstanding.length} unpaid invoice{outstanding.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="bg-white p-5 sm:p-6">
            <div className="eyebrow">Total overdue</div>
            <div className="tabular text-2xl sm:text-3xl mt-2 text-[color:var(--overdue)] break-words">
              {INR(data.overdue_amount || 0)}
            </div>
            <div className="text-xs text-ink-2 mt-2">
              {data.overdue_count || 0} past due
            </div>
          </div>
        </div>

        <div>
          <div className="eyebrow mb-2">Unpaid invoices</div>
          <div className="bg-white overflow-x-auto">
            {outstanding.length === 0 ? (
              <div className="p-8 text-center text-ink-2 text-sm">
                No outstanding invoices. You&apos;re all clear.
              </div>
            ) : (
              <table className="w-full text-[13px] min-w-[960px]">
                <thead>
                  <tr className="row-divider text-left">
                    <th className="px-4 py-2.5 eyebrow whitespace-nowrap">Invoice #</th>
                    <th className="px-4 py-2.5 eyebrow whitespace-nowrap">Invoice Date</th>
                    <th className="px-4 py-2.5 eyebrow whitespace-nowrap">Billing Period</th>
                    <th className="px-4 py-2.5 eyebrow whitespace-nowrap">Due Date</th>
                    <th className="px-4 py-2.5 eyebrow whitespace-nowrap">Due By</th>
                    <th className="px-4 py-2.5 eyebrow text-right whitespace-nowrap">Amount</th>
                    <th className="px-4 py-2.5 eyebrow whitespace-nowrap">Status</th>
                    <th className="px-4 py-2.5 eyebrow text-right whitespace-nowrap">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.map((i) => {
                    const hasPayments = (i.payments && i.payments.length > 0);
                    const isOpen = !!expanded[i.id];
                    return (
                      <Fragment key={i.id}>
                        <tr className="row-divider">
                          <td className="px-4 py-2.5 tabular whitespace-nowrap relative">
                            {hasPayments && (
                              <button
                                type="button"
                                onClick={() => toggle(i.id)}
                                className="absolute left-0.5 top-1/2 -translate-y-1/2 text-ink-2 hover:text-ink"
                                aria-label="Toggle payment history"
                                data-testid={`portal-toggle-${i.id}`}
                              >
                                <ChevronRight
                                  className={`w-3 h-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
                                />
                              </button>
                            )}
                            {i.invoice_number}
                          </td>
                            <td className="px-4 py-2.5 whitespace-nowrap">{fmtDMY(i.invoice_date)}</td>
                            <td className="px-4 py-2.5 text-ink-2 whitespace-nowrap">
                              {fmtDMY(i.billing_period_start)} → {fmtDMY(i.billing_period_end)}
                            </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">{fmtDMY(i.due_date)}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <DueByChip dueDate={i.due_date} />
                          </td>
                          <td className="px-4 py-2.5 tabular text-right whitespace-nowrap">
                            <div>{INR(i.amount)}</div>
                            {i.status === "partial" && (
                              <div className="text-[11px] text-[color:var(--pending)] font-semibold">
                                {INR(i.remaining_amount)} pending
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge status={i.status} isOverdue={i.is_overdue} />
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {i.pdf_file_id ? (
                              <button
                                onClick={() => download(i.pdf_file_id, i.invoice_number)}
                                className="inline-flex items-center gap-1 text-[color:var(--aisensy-dark)] hover:underline text-xs"
                              >
                                <Download className="w-3.5 h-3.5" /> Download
                              </button>
                            ) : (
                              <span className="text-ink-2 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                        {hasPayments && isOpen && (
                          <tr className="row-divider bg-[color:var(--bone)]" data-testid={`portal-payments-${i.id}`}>
                            <td colSpan={8} className="px-8 py-4">
                              <div className="eyebrow mb-2">Payment history</div>
                              <ol className="relative ml-2 border-l border-line space-y-2">
                                {(sortedPaymentsByInvoiceId[i.id] || [])
                                  .map((p, idx) => (
                                    <li
                                      key={p.utr_number || p.payment_id || `${p.payment_date || ""}-${p.amount || ""}-${idx}`}
                                      className="pl-4 relative"
                                    >
                                      <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[color:var(--aisensy-green)]" />
                                      <div className="text-sm tabular">
                                        <span className="font-semibold">{INR(p.amount)}</span>
                                        <span className="text-ink-2 text-[11px] ml-2">on {fmtDMY(p.payment_date)}</span>
                                      </div>
                                      <div className="text-[11px] text-ink-2 tabular mt-0.5">
                                        {p.utr_number && <>UTR <span className="text-ink font-medium">{p.utr_number}</span></>}
                                        {p.utr_number && p.payment_id ? " · " : ""}
                                        {p.payment_id && <>Payment ID <span className="text-ink font-medium">{p.payment_id}</span></>}
                                      </div>
                                    </li>
                                  ))}
                              </ol>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {paid.length > 0 && (
          <div>
            <div className="eyebrow mb-2">Recently paid</div>
            <div className="bg-white overflow-x-auto">
              <table className="w-full text-[13px] min-w-[860px]">
                <thead>
                  <tr className="row-divider text-left">
                    <th className="px-4 py-2.5 eyebrow">Invoice #</th>
                    <th className="px-4 py-2.5 eyebrow">Invoice Date</th>
                    <th className="px-4 py-2.5 eyebrow">Paid On</th>
                    <th className="px-4 py-2.5 eyebrow">On Time?</th>
                    <th className="px-4 py-2.5 eyebrow">Payment Ref</th>
                    <th className="px-4 py-2.5 eyebrow text-right">Amount</th>
                    <th className="px-4 py-2.5 eyebrow text-right">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {paid.map((i) => (
                    <tr key={i.id} className="row-divider">
                      <td className="px-4 py-2.5">{i.invoice_number}</td>
                      <td className="px-4 py-2.5">{fmtDMY(i.invoice_date)}</td>
                      <td className="px-4 py-2.5">{fmtDMY(i.payment_date)}</td>
                      <td className="px-4 py-2.5">
                        <PaidChip dueDate={i.due_date} paymentDate={i.payment_date} />
                      </td>
                      <td className="px-4 py-2.5 tabular text-ink-2">
                        {(() => {
                          if (i.utr_number) return `UTR ${i.utr_number}`;
                          if (i.payment_id) return `ID ${i.payment_id}`;
                          return "-";
                        })()}
                      </td>
                      <td className="px-4 py-2.5 tabular text-right">{INR(i.amount)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {i.pdf_file_id ? (
                          <button
                            onClick={() => download(i.pdf_file_id, i.invoice_number)}
                            className="inline-flex items-center gap-1 text-[color:var(--aisensy-dark)] hover:underline text-xs"
                          >
                            <FileText className="w-3.5 h-3.5" /> PDF
                          </button>
                        ) : (
                          <span className="text-ink-2 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-xs text-ink-2 pt-6">
          This is a read-only statement portal. For any queries, please reach out to your AiSensy account manager.
        </div>
      </main>
    </div>
  );
}
